/**
 * Arthaus Cart Drawer
 * Phase: 6
 *
 * Slide-out cart tray (right rail desktop / full-screen mobile) with:
 *  - open/close + focus trap + scroll lock (mirrors sections/mobile-drawer.liquid)
 *  - live qty / remove via Shopify bundled Section Rendering (/cart/change.js)
 *  - one Picasso-concierge "one more piece" recommendation (POST {apiBase}/api/chat)
 *  - an inline quick variant configurator (framed/print + size + frame-color/border)
 *    that resolves to a real Shopify variant and adds via /cart/add.js
 *
 * Public API (window.ArthausCart):
 *   init(opts)        — { apiBase, recommend, sectionId }; binds once
 *   open() / close()
 *   openWithItem()    — refresh the drawer from the server, then open (used post add-to-cart)
 */

(function () {
  'use strict';

  var SECTION = 'cart-drawer';
  var cfg = { apiBase: '', recommend: true };
  var bound = false;
  var previousFocus = null;
  var reco = { shownToken: null, product: null, pair: null, loading: false };

  // ── DOM helpers ──────────────────────────────────────────────────────────
  function root() { return document.querySelector('[data-cart-drawer]'); }
  function panel() { return document.querySelector('[data-cart-panel]'); }
  function backdrop() { return document.querySelector('[data-cart-backdrop]'); }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    return '$' + Math.round(cents / 100).toLocaleString();
  }
  function baseHandle(h) { return (h || '').replace(/-no-frame$/, ''); }

  // ── Open / close ─────────────────────────────────────────────────────────
  function isOpen() { var r = root(); return r && !r.hidden; }

  function open() {
    var r = root(), p = panel(), b = backdrop();
    if (!r || !p) return;
    previousFocus = document.activeElement;
    r.hidden = false;
    r.classList.add('pointer-events-auto');
    r.classList.remove('pointer-events-none');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      if (b) { b.classList.remove('opacity-0'); b.classList.add('opacity-100'); }
      p.classList.remove('translate-x-full');
      p.classList.add('translate-x-0');
    });

    var closeBtn = p.querySelector('[data-cart-close]');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 300);

    document.querySelectorAll('[data-cart-open]').forEach(function (btn) { btn.setAttribute('aria-expanded', 'true'); });
    refreshReco();
  }

  function close() {
    var r = root(), p = panel(), b = backdrop();
    if (!r || !p) return;
    if (b) { b.classList.remove('opacity-100'); b.classList.add('opacity-0'); }
    p.classList.remove('translate-x-0');
    p.classList.add('translate-x-full');
    document.body.style.overflow = '';
    document.querySelectorAll('[data-cart-open]').forEach(function (btn) { btn.setAttribute('aria-expanded', 'false'); });

    setTimeout(function () {
      r.hidden = true;
      r.classList.remove('pointer-events-auto');
      r.classList.add('pointer-events-none');
      if (previousFocus && previousFocus.focus) previousFocus.focus();
    }, 300);
  }

  // ── Region swap (Section Rendering) ───────────────────────────────────────
  // Swap the live items + footer regions from a freshly-rendered section's HTML.
  function swapFromSectionHTML(html) {
    var r = root();
    if (!r || !html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    ['[data-cart-items-region]', '[data-cart-footer-region]'].forEach(function (sel) {
      var fresh = doc.querySelector(sel), cur = r.querySelector(sel);
      if (fresh && cur) cur.innerHTML = fresh.innerHTML;
    });
    var freshCount = doc.querySelector('[data-cart-count]');
    if (freshCount) setCount(freshCount.textContent.replace(/[()]/g, '').trim());
  }

  // GET-render the section (used when we don't already have bundled section HTML).
  function refreshDrawer() {
    return fetch('/?sections=' + SECTION, { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (data) { swapFromSectionHTML(data && data[SECTION]); })
      .catch(function () { /* leave current DOM intact */ });
  }

  function setCount(n) {
    var label = '(' + n + ')';
    var dc = document.querySelector('[data-cart-count]');
    if (dc) dc.textContent = label;
    // Header badge — toggle the `hidden` class (orders after `flex`, so it wins);
    // do NOT use the [hidden] attribute here (a `flex` class would beat it).
    document.querySelectorAll('[data-header-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('hidden', (parseInt(n, 10) || 0) === 0);
    });
  }

  // ── Cart mutations ───────────────────────────────────────────────────────
  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: [SECTION], sections_url: window.location.pathname })
    })
      .then(function (res) { if (!res.ok) throw new Error('change failed'); return res.json(); })
      .then(function (data) {
        if (data.sections && data.sections[SECTION]) swapFromSectionHTML(data.sections[SECTION]);
        else refreshDrawer();
        setCount(data.item_count != null ? data.item_count : '');
        refreshReco(); // cache-keyed on the cart's handle-set: instant if unchanged, re-curates on add/remove
      });
  }

  function lineQty(key) {
    var line = document.querySelector('[data-cart-line][data-line-key="' + cssEsc(key) + '"]');
    if (!line) return 1;
    var q = line.querySelector('[data-cart-qty]');
    return q ? (parseInt(q.textContent, 10) || 1) : 1;
  }
  function cssEsc(s) { return String(s).replace(/"/g, '\\"'); }

  // ── Concierge recommendation ─────────────────────────────────────────────
  var productCache = {}; // handle -> product JSON | null (also warms the configurator)

  function recoEl() { return document.querySelector('[data-cart-reco]'); }
  function hideReco() { var el = recoEl(); if (el) { el.classList.add('hidden'); el.innerHTML = ''; } reco.shownToken = null; }

  // The rec depends on WHICH artworks are in the cart, not their quantities — so key the
  // cache on the sorted set of (base) handles. Qty changes don't bust it; add/remove does.
  function cartTokenOf(cart) {
    var hs = (cart.items || []).map(function (it) { return baseHandle(it.handle); });
    hs.sort();
    return hs.join('|') || 'empty';
  }
  function ssGet(token) {
    try { var v = sessionStorage.getItem('arthaus.cartReco.' + token); return v ? JSON.parse(v) : null; }
    catch (e) { return null; }
  }
  function ssSet(token, obj) {
    try { sessionStorage.setItem('arthaus.cartReco.' + token, JSON.stringify(obj)); }
    catch (e) { /* private mode / quota — fine, we just recompute */ }
  }

  function titleCase(s) {
    return String(s || '').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function hasAvailable(prod) {
    if (!prod || !prod.variants) return false;
    for (var i = 0; i < prod.variants.length; i++) if (prod.variants[i].available) return true;
    return false;
  }

  // Resolve a concierge handle to its framed + print Shopify products ON THIS store.
  function resolvePair(handle) {
    var base = baseHandle(handle);
    return Promise.all([fetchProduct(base), fetchProduct(base + '-no-frame')])
      .then(function (r) { return { framed: r[0], print: r[1] }; });
  }

  // Walk the concierge picks; return the first that isn't already in the cart AND actually
  // resolves to a buyable product here (the graph can return stale/renamed handles).
  function pickValid(products, cartHandles) {
    var idx = 0;
    function next() {
      if (idx >= products.length) return Promise.resolve(null);
      var p = products[idx++];
      if (!p.handle || cartHandles[baseHandle(p.handle)]) return next();
      return resolvePair(p.handle).then(function (pair) {
        var prod = pair.framed || pair.print;
        if (prod && hasAvailable(prod)) {
          return {
            product: p,
            framedHandle: pair.framed ? pair.framed.handle : null,
            printHandle: pair.print ? pair.print.handle : null
          };
        }
        return next();
      });
    }
    return next();
  }

  // Compute a fully-validated recommendation object for a cart (or null).
  // Uses /api/recommend — the concierge seeds retrieval from the cart artworks THEMSELVES
  // (their graph concepts + image embeddings), returning genuinely complementary pieces,
  // per-product shared concepts (`why`), a cart-level concept profile, and a prose rationale.
  function computeReco(cart) {
    var cartHandles = {};
    var handles = [];
    var titles = [];
    (cart.items || []).forEach(function (it) {
      cartHandles[baseHandle(it.handle)] = true;
      if (it.handle) handles.push(it.handle);
      if (it.product_title) titles.push(it.product_title);
    });
    return recommend(handles, titles).then(function (data) {
      var shared = (data && data.shared_concepts) || [];
      return pickValid((data && data.products) || [], cartHandles).then(function (hit) {
        if (!hit) return null;
        var p = hit.product;
        var facets = (p.why && p.why.length ? p.why : shared).filter(Boolean).slice(0, 3).map(titleCase);
        return {
          product: { handle: p.handle, title: p.title, artist: p.artist || '', image: p.image || '', price_formatted: p.price_formatted || '' },
          prose: ((data && data.concierge && data.concierge.message) || '').trim() || synthWhy(p),
          concepts: facets,
          framedHandle: hit.framedHandle,
          printHandle: hit.printHandle
        };
      });
    });
  }

  // Background: compute + cache for the current cart if we don't already have it. Runs on
  // idle and after cart changes so the tray's recommendation is ready before it's opened.
  function prefetchReco() {
    if (!cfg.recommend || !cfg.apiBase || reco.loading) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) return;
        var token = cartTokenOf(cart);
        if (ssGet(token)) { renderFromCache(token); return; }
        reco.loading = true;
        return computeReco(cart).then(function (r) {
          reco.loading = false;
          ssSet(token, r || { empty: true });
          renderFromCache(token);
        });
      })
      .catch(function () { reco.loading = false; });
  }

  // On open: render instantly from cache; otherwise compute live with a subtle indicator.
  function refreshReco() {
    if (!cfg.recommend || !cfg.apiBase) return hideReco();
    var el = recoEl();
    if (!el || !isOpen()) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) return hideReco();
        var token = cartTokenOf(cart);
        var cached = ssGet(token);
        if (cached) {
          if (cached.empty) return hideReco();
          if (reco.shownToken === token && el && !el.classList.contains('hidden')) return; // already shown
          return renderReco(el, cached, token);
        }
        showRecoLoading(el);
        if (reco.loading) return;
        reco.loading = true;
        return computeReco(cart).then(function (r) {
          reco.loading = false;
          ssSet(token, r || { empty: true });
          if (!isOpen()) return;
          if (r) renderReco(el, r, token); else hideReco();
        });
      })
      .catch(function () { reco.loading = false; hideReco(); });
  }

  function renderFromCache(token) {
    var el = recoEl();
    if (!el || !isOpen()) return;
    var cached = ssGet(token);
    if (!cached || cached.empty) return hideReco();
    if (reco.shownToken === token && !el.classList.contains('hidden')) return;
    renderReco(el, cached, token);
  }

  function showRecoLoading(el) {
    if (reco.shownToken) return; // don't replace an already-rendered card with a spinner
    el.classList.remove('hidden');
    el.innerHTML = '<div class="flex items-center gap-2.5 py-1 text-warm-gray">' +
      '<div class="w-4 h-4 border-2 border-bronze/30 border-t-bronze rounded-full animate-spin"></div>' +
      '<span class="font-body text-xs animate-pulse">The concierge is curating a pairing…</span></div>';
  }

  function recommend(handles, titles) {
    return fetch(cfg.apiBase.replace(/\/$/, '') + '/api/recommend', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-Arthaus-Client': 'storefront' },
      body: JSON.stringify({ cart_handles: handles, seed_titles: titles || [], limit: 8, concierge: true })
    }).then(function (res) { if (!res.ok) throw new Error('recommend ' + res.status); return res.json(); });
  }

  // Fallback "why" when the endpoint returns no prose — synthesized from the product's
  // shared facets / artist so the card always reads cleanly.
  function synthWhy(p) {
    var facets = (p.why || []).filter(Boolean).slice(0, 3);
    if (facets.length) {
      return 'Pairs with your cart through a shared language of ' + listPhrase(facets) + '.';
    }
    return (p.artist ? p.artist + '’s work ' : 'This piece ') +
      'echoes the tone of your current selection — a quiet complement rather than a repeat.';
  }
  function listPhrase(a) {
    if (a.length === 1) return a[0];
    return a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1];
  }

  var SPARKLE = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" class="text-bronze shrink-0"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>';

  // Layout: image + title + CTA are the primary action. Below sits the curator's prose
  // rationale and 2–3 concept tiles (the shared visual language) — present and beautiful,
  // but secondary to the buy action.
  function renderReco(el, r, token) {
    var p = r.product;
    reco.product = p;
    reco.pair = { framedHandle: r.framedHandle, printHandle: r.printHandle };
    reco.shownToken = token;
    var href = '/products/' + p.handle;

    var conceptTiles = '';
    if (r.concepts && r.concepts.length) {
      var chips = '';
      for (var i = 0; i < r.concepts.length; i++) {
        chips += '<span class="font-body text-xs text-charcoal bg-parchment border border-border rounded-full px-3 py-1">' + escapeHtml(r.concepts[i]) + '</span>';
      }
      conceptTiles = '<div class="flex flex-wrap gap-2 mt-3">' + chips + '</div>';
    }

    el.innerHTML =
      '<div class="flex items-center gap-2 mb-4">' + SPARKLE +
        '<p class="font-mono text-xs text-bronze uppercase tracking-wider">The concierge suggests</p>' +
      '</div>' +
      '<div class="flex gap-4 items-start">' +
        '<a href="' + href + '" class="shrink-0 w-24 rounded-md overflow-hidden" aria-label="' + escapeHtml(p.title) + '">' +
          (p.image ? '<img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" width="96" height="128" class="w-full h-auto aspect-[3/4] object-cover" loading="lazy">' : '<span class="block aspect-[3/4] bg-parchment"></span>') +
        '</a>' +
        '<div class="flex-1 min-w-0">' +
          '<a href="' + href + '" class="font-display text-lg text-charcoal font-light leading-snug hover:text-bronze transition-colors line-clamp-2">' + escapeHtml(p.title) + '</a>' +
          (p.artist ? '<p class="font-body text-xs text-warm-gray mt-0.5 truncate">' + escapeHtml(p.artist) + '</p>' : '') +
          (p.price_formatted ? '<p class="font-body text-sm text-charcoal mt-1.5">' + escapeHtml(p.price_formatted) + '</p>' : '') +
          '<button type="button" data-reco-add class="mt-3 flex items-center justify-center gap-1.5 w-full font-body text-xs uppercase tracking-wide text-white bg-charcoal hover:bg-bronze transition-colors rounded-full py-2.5">Add to cart</button>' +
        '</div>' +
      '</div>' +
      '<div data-reco-config class="mt-3"></div>' +
      (r.prose ? '<p class="mt-4 font-body text-sm text-warm-gray leading-relaxed">' + escapeHtml(r.prose) + '</p>' : '') +
      conceptTiles;
    el.classList.remove('hidden');
  }

  // ── Inline quick configurator ────────────────────────────────────────────
  var conf = null; // { products:{framed,print}, mode, names:[], values:[[..]], selected:[] }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle] !== undefined) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (j) { productCache[handle] = j; return j; })
      .catch(function () { productCache[handle] = null; return null; });
  }

  function optionNames(prod) {
    return (prod.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
  }
  // Distinct option values per position, derived from variants (robust + availability-true).
  function optionValues(prod) {
    var names = optionNames(prod), cols = [];
    for (var i = 0; i < names.length; i++) {
      var seen = {}, vals = [];
      for (var v = 0; v < prod.variants.length; v++) {
        var val = prod.variants[v].options[i];
        if (val != null && !seen[val]) { seen[val] = 1; vals.push(val); }
      }
      cols.push(vals);
    }
    return cols;
  }
  function findVariant(variants, selected) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i], ok = true;
      for (var j = 0; j < selected.length; j++) { if (v.options[j] !== selected[j]) { ok = false; break; } }
      if (ok) return v;
    }
    return variants.length === 1 ? variants[0] : null;
  }
  function firstAvailable(prod) {
    for (var i = 0; i < prod.variants.length; i++) if (prod.variants[i].available) return prod.variants[i];
    return prod.variants[0];
  }

  function openConfigurator() {
    var slot = document.querySelector('[data-reco-config]');
    var addBtn = document.querySelector('[data-reco-add]');
    if (!slot || !reco.product) return;
    if (addBtn) addBtn.disabled = true;
    slot.innerHTML = '<div class="flex items-center gap-2 text-warm-gray py-2"><div class="w-4 h-4 border-2 border-bronze/30 border-t-bronze rounded-full animate-spin"></div><span class="font-body text-xs">Loading options…</span></div>';

    // Handles were validated + warmed into productCache during prefetch, so these resolve
    // instantly. Fall back to deriving from the pick's handle if the pair wasn't recorded.
    var pair = reco.pair || {};
    var fh = pair.framedHandle || baseHandle(reco.product.handle);
    var ph = pair.printHandle || (baseHandle(reco.product.handle) + '-no-frame');
    Promise.all([fetchProduct(fh), fetchProduct(ph)]).then(function (res) {
      var framed = res[0], print = res[1];
      if (!framed && !print) {
        slot.innerHTML = '<p class="font-body text-xs text-warm-gray">Choose options on the <a href="/products/' + escapeHtml(reco.product.handle) + '" class="underline text-bronze">product page</a>.</p>';
        return;
      }
      conf = { products: { framed: framed, print: print }, mode: framed ? 'framed' : 'print' };
      buildSelection();
      renderConfigurator(slot);
      if (addBtn) addBtn.classList.add('hidden'); // configurator replaces the CTA
    });
  }

  function activeProduct() { return conf.products[conf.mode]; }

  function buildSelection() {
    var prod = activeProduct();
    conf.names = optionNames(prod);
    conf.values = optionValues(prod);
    var fv = firstAvailable(prod);
    conf.selected = (fv && fv.options) ? fv.options.slice() : [];
  }

  function renderConfigurator(slot) {
    var prod = activeProduct();
    var html = '<div class="rounded-lg border border-border bg-parchment-light p-4">';

    // Framed / print toggle
    if (conf.products.framed && conf.products.print) {
      html += '<div class="inline-flex rounded-full border border-border bg-white p-0.5 mb-4" role="group" aria-label="Frame option">';
      html += toggleBtn('framed', 'Framed');
      html += toggleBtn('print', 'Print only');
      html += '</div>';
    }

    // Option groups (skip a group with a single value)
    for (var i = 0; i < conf.names.length; i++) {
      if (conf.values[i].length <= 1) continue;
      html += '<div class="mb-3" data-cfg-group data-cfg-pos="' + i + '">';
      html += '<p class="font-mono text-xs text-mute uppercase tracking-wider mb-1.5">' + escapeHtml(conf.names[i]) + '</p>';
      html += '<div class="flex flex-wrap gap-1.5">';
      for (var j = 0; j < conf.values[i].length; j++) {
        var val = conf.values[i][j];
        var on = conf.selected[i] === val;
        html += '<button type="button" data-cfg-value data-cfg-pos="' + i + '" data-cfg-val="' + escapeHtml(val) + '" ' +
          'class="font-body text-xs rounded-full border px-3 py-1.5 transition-colors ' +
          (on ? 'bg-charcoal text-white border-charcoal' : 'bg-white text-charcoal border-border hover:border-charcoal') + '">' +
          escapeHtml(val) + '</button>';
      }
      html += '</div></div>';
    }

    var v = findVariant(prod.variants, conf.selected);
    var ok = v && v.available;
    html += '<button type="button" data-reco-confirm class="mt-2 flex items-center justify-center gap-2 w-full font-body text-sm uppercase tracking-wide text-white bg-charcoal hover:bg-bronze disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-full py-3"' +
      (ok ? '' : ' disabled') + '>' +
      (ok ? 'Add to cart · ' + money(v.price) : (v ? 'Sold out' : 'Select options')) +
      '</button>';
    html += '</div>';
    slot.innerHTML = html;
  }

  function toggleBtn(mode, label) {
    var on = conf.mode === mode;
    return '<button type="button" data-cfg-mode="' + mode + '" class="font-body text-xs uppercase tracking-wide rounded-full px-3.5 py-1.5 transition-colors ' +
      (on ? 'bg-charcoal text-white' : 'text-warm-gray hover:text-charcoal') + '">' + label + '</button>';
  }

  function confirmAdd(btn) {
    var prod = activeProduct();
    var v = findVariant(prod.variants, conf.selected);
    if (!v || !v.available) return;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: v.id, quantity: 1, sections: [SECTION], sections_url: window.location.pathname })
    })
      .then(function (res) { if (!res.ok) throw new Error('add failed'); return res.json(); })
      .then(function (data) {
        if (data.sections && data.sections[SECTION]) swapFromSectionHTML(data.sections[SECTION]);
        else refreshDrawer();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        reco.shownToken = null; conf = null;
        refreshReco(); // re-curate for the larger cart
        var scroll = document.querySelector('[data-cart-scroll]');
        if (scroll) scroll.scrollTop = 0;
      })
      .catch(function () { btn.disabled = false; btn.textContent = 'Error — try again'; });
  }

  // ── Event wiring (delegated; survives region swaps) ───────────────────────
  function bindEvents() {
    if (bound) return;
    bound = true;

    // Open from the header cart icon (and any [data-cart-open])
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-cart-open]');
      if (opener) { e.preventDefault(); open(); return; }
    });

    var r = root();
    if (!r) return;

    // Close (button, backdrop, empty-state CTA)
    r.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-close]')) { close(); return; }
      if (e.target === backdrop()) { close(); return; }

      var dec = e.target.closest('[data-cart-qty-decrease]');
      var inc = e.target.closest('[data-cart-qty-increase]');
      var rem = e.target.closest('[data-cart-remove]');
      if (dec) { var k = dec.getAttribute('data-line-key'); changeLine(k, Math.max(0, lineQty(k) - 1)); return; }
      if (inc) { var k2 = inc.getAttribute('data-line-key'); changeLine(k2, lineQty(k2) + 1); return; }
      if (rem) { changeLine(rem.getAttribute('data-line-key'), 0); return; }

      // Recommendation + configurator
      if (e.target.closest('[data-reco-add]')) { openConfigurator(); return; }
      var modeBtn = e.target.closest('[data-cfg-mode]');
      if (modeBtn) {
        conf.mode = modeBtn.getAttribute('data-cfg-mode');
        buildSelection();
        renderConfigurator(document.querySelector('[data-reco-config]'));
        return;
      }
      var valBtn = e.target.closest('[data-cfg-value]');
      if (valBtn) {
        var pos = parseInt(valBtn.getAttribute('data-cfg-pos'), 10);
        conf.selected[pos] = valBtn.getAttribute('data-cfg-val');
        renderConfigurator(document.querySelector('[data-reco-config]'));
        return;
      }
      var confirm = e.target.closest('[data-reco-confirm]');
      if (confirm && !confirm.disabled) { confirmAdd(confirm); return; }
    });

    // Escape closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });

    // Focus trap
    r.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var p = panel();
      var focusable = p.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      var first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  // ── Public API ────────────────────────────────────────────────────────────
  window.ArthausCart = {
    init: function (opts) {
      opts = opts || {};
      cfg.apiBase = opts.apiBase || '';
      cfg.recommend = opts.recommend !== false;
      bindEvents();
      // Curate the recommendation in the background so it's cached before the tray opens,
      // and re-curate whenever the cart changes (add-to-cart elsewhere on the page).
      if (cfg.recommend && cfg.apiBase) {
        if (window.requestIdleCallback) requestIdleCallback(prefetchReco, { timeout: 3000 });
        else setTimeout(prefetchReco, 1500);
        document.addEventListener('cart:updated', function () { setTimeout(prefetchReco, 400); });
      }
    },
    open: open,
    close: close,
    openWithItem: function () {
      refreshDrawer().then(open);
    }
  };
})();
